import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";

var _class, _class2, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _descriptor6, _descriptor7, _descriptor8, _descriptor9, _class3;

import { action, observable } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import { Carousel, defaultGetStatusAnnouncement } from './carousel.react-component';
/** Event dispatched when the `AutoplayCarousel` has a change to it's autoplay interval */

// Left position of a scroll item relative to the scroll port
const itemScrollLeft = (scrollPort, index) => {
  const item = scrollPort.children[index];
  const {
    left: scrollPortLeft
  } = scrollPort.getBoundingClientRect();
  const {
    left: itemLeft
  } = item.getBoundingClientRect();
  return itemLeft - scrollPortLeft;
}; // AutoPlayCarousel assumes equally-sized items, with equal gaps. So, the scroll
// distance to cover one item is constant, regardless of item.


const scrollDistance = scrollPort => {
  const children = scrollPort.children;
  const {
    left: firstItemLeft
  } = children[0].getBoundingClientRect();
  const {
    left: secondItemLeft
  } = children[1].getBoundingClientRect();
  return secondItemLeft - firstItemLeft;
};

const virtualSlideDataAttribute = 'data-auto-play-carouse-virtual-slide';
/**
 * The AutoPlayCarousel component.
 *
 * @remarks
 * This component currently only works on carousels that show one item at a time.
 * To make auto play possible, we insert the last item of the carousel in the front of the carousel
 * and the first item of the carousel in the end of the carousel to make an infinite scroll.
 * Then, we update the scroll port position to the real first or last item.
 */

export let AutoPlayCarousel = observer(_class = (_class2 = (_class3 = class AutoPlayCarousel extends React.Component {
  constructor() {
    super(...arguments);

    _initializerDefineProperty(this, "renderVirtualItems", _descriptor, this);

    _initializerDefineProperty(this, "canScroll", _descriptor2, this);

    _initializerDefineProperty(this, "allowAutoPlay", _descriptor3, this);

    _initializerDefineProperty(this, "isPaused", _descriptor4, this);

    this.autoPlayInterval = void 0;
    this.ref = /*#__PURE__*/React.createRef();

    _initializerDefineProperty(this, "handleCarouselMutation", _descriptor5, this);

    this.getStatusAnnouncement = _ref => {
      let {
        ninterpolate,
        slides
      } = _ref;
      const nonVirtualSlides = slides.filter(slide => !slide.getAttribute(virtualSlideDataAttribute));
      return defaultGetStatusAnnouncement({
        ninterpolate,
        slides: nonVirtualSlides
      });
    };

    _initializerDefineProperty(this, "handleIntervalUpdate", _descriptor6, this);

    this.advanceCarousel = () => {
      var _this$ref$current;

      const scrollPort = (_this$ref$current = this.ref.current) === null || _this$ref$current === void 0 ? void 0 : _this$ref$current.scrollPortRef.current;

      if (scrollPort) {
        scrollPort.scrollBy({
          left: scrollDistance(scrollPort),
          behavior: 'smooth'
        });
      }
    };

    _initializerDefineProperty(this, "resumeCarousel", _descriptor7, this);

    _initializerDefineProperty(this, "pauseCarousel", _descriptor8, this);

    _initializerDefineProperty(this, "onInteraction", _descriptor9, this);
  }

  componentDidMount() {
    const hasMoreThanOneChild = this.itemNodes.length > 1;

    if (hasMoreThanOneChild) {
      // AutoPlayCarousel only works on carousels that show one item at a time.
      // Assume the item takes up the entire width of the carousel and
      // compare the width of an item to the carousel width.
      if (this.ref.current) {
        const scrollPort = this.ref.current.scrollPortRef.current;
        const firstChild = scrollPort === null || scrollPort === void 0 ? void 0 : scrollPort.firstChild;
        const carouselItemWidth = firstChild.clientWidth;
        const roundedCarouselItemWidth = Math.ceil(carouselItemWidth / 5) * 5;
        const carouselWidth = scrollPort === null || scrollPort === void 0 ? void 0 : scrollPort.clientWidth;

        if (carouselWidth) {
          const roundedCarouselWidth = Math.ceil(carouselWidth / 5) * 5;

          if (carouselWidth > 0 && roundedCarouselItemWidth === roundedCarouselWidth) {
            this.canScroll = true;
            this.isPaused = false; // Scroll past the first virtual item to the first real item

            scrollPort.scrollLeft = scrollDistance(scrollPort);
            this.resumeCarousel();
          }
        }
      }
    } else {
      this.renderVirtualItems = false;
    }
  }

  componentWillUnmount() {
    this.pauseCarousel();
  } // Virtual items are prepended and appended to the real items to allow for seemingly
  // continuous infinite scrolling.


  // Merges slides prop and legacy children; only one of the two should be
  // used. This normalizes them regardless of which is used.
  get itemNodes() {
    return [...React.Children.toArray(this.props.children), ...React.Children.toArray(this.props.slides)];
  } // If allowed, append virtual items to the carousel for the appearance of continuous
  // infinite scrolling.


  get items() {
    const children = this.itemNodes;

    if (this.renderVirtualItems) {
      // Append the first item to the end, and prepend the last item to the front,
      // so the last item auto plays to the first, and the first item scrolls to last item
      // when the previous button is clicked if auto play is active.
      const lastItem = children[children.length - 1];
      const firstItem = children[0]; // Provide a unique key for cloned items since slides passed through
      // slides prop will not be wrapped in individual nodes within Carousel.

      children.unshift( /*#__PURE__*/React.cloneElement(lastItem, {
        key: 'virtual-last-item',
        [virtualSlideDataAttribute]: true
      }));
      children.push( /*#__PURE__*/React.cloneElement(firstItem, {
        key: 'virtual-first-item',
        [virtualSlideDataAttribute]: true
      }));
    }

    return children;
  }

  pageCount(scrollPort) {
    const extraWidth = scrollPort.clientWidth * 2;
    return Math.floor((scrollPort.scrollWidth - extraWidth) / scrollPort.clientWidth);
  }

  visiblePageIndex(scrollPort) {
    // Subtract clientWidth to account for first virtual item
    return Math.floor((scrollPort.scrollLeft - scrollPort.clientWidth) / scrollPort.clientWidth);
  }

  render() {
    const {
      prioritizeTouch,
      allowScroll,
      onIntervalUpdate,
      intervalDuration,
      ...props
    } = this.props;
    const allowScrolling = allowScroll == true ? allowScroll : prioritizeTouch;
    const autoPlayControls = prioritizeTouch ? {
      onTouchStart: this.canScroll ? this.onInteraction : undefined,
      onTouchEnd: this.canScroll ? this.resumeCarousel : undefined
    } : {
      onMouseEnter: this.canScroll ? this.pauseCarousel : undefined,
      onMouseLeave: this.canScroll ? this.resumeCarousel : undefined,
      onMouseDown: this.canScroll ? this.onInteraction : undefined
    }; // Retain backwards compatibility for slides passed as children. They
    // will not be wrapped in CarouselSlide components and should not be passed
    // via slides prop.

    const slides = this.props.slides ? this.items : undefined;
    const children = !this.props.slides ? this.items : undefined;
    return /*#__PURE__*/React.createElement("div", Object.assign({}, autoPlayControls, {
      onFocus: this.onInteraction
    }), /*#__PURE__*/React.createElement(Carousel, Object.assign({}, props, {
      showPager: this.props.showPager && this.itemNodes.length > 1,
      ariaLive: this.isPaused ? 'polite' : 'off',
      allowScroll: allowScrolling,
      isInfiniteScroll: true,
      pageByFullWidth: true,
      gridFullWidthItems: true,
      getStatusAnnouncement: this.getStatusAnnouncement,
      onMutation: this.handleCarouselMutation,
      ref: this.ref,
      slides: slides
    }), children));
  }

}, _class3.defaultProps = { ...Carousel.defaultProps,
  showPager: undefined,
  prioritizeTouch: false,
  intervalDuration: 7000
}, _class3), (_applyDecoratedDescriptor(_class2.prototype, "componentDidMount", [action], Object.getOwnPropertyDescriptor(_class2.prototype, "componentDidMount"), _class2.prototype), _descriptor = _applyDecoratedDescriptor(_class2.prototype, "renderVirtualItems", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return true;
  }
}), _descriptor2 = _applyDecoratedDescriptor(_class2.prototype, "canScroll", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor3 = _applyDecoratedDescriptor(_class2.prototype, "allowAutoPlay", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return true;
  }
}), _descriptor4 = _applyDecoratedDescriptor(_class2.prototype, "isPaused", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return true;
  }
}), _descriptor5 = _applyDecoratedDescriptor(_class2.prototype, "handleCarouselMutation", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return event => {
      if (this.canScroll && this.ref.current) {
        const carousel = this.ref.current;
        const scrollPort = carousel.scrollPortRef.current; // Update the scroll port position to the real first or last item.

        if (scrollPort) {
          var _this$props$onMutatio, _this$props;

          if (carousel.isLastPage) {
            // Last page is the last virtual item. Assume that the scroll
            // arrived here from the left, so jump back to the first
            // real item.
            scrollPort.scrollLeft = scrollDistance(scrollPort);
          } else if (carousel.isFirstPage) {
            // First page is the first virtual item. Assume that the scroll
            // arrived here from the right, so jump to the last real item
            scrollPort.scrollLeft = itemScrollLeft(scrollPort, // Note: itemNodes.length === items.length - 2;
            this.itemNodes.length);
          }

          const mutationEvent = {
            isLastPage: event.isLastPage,
            isPageable: event.isPageable,
            pageCount: this.pageCount(scrollPort),
            visiblePageIndex: this.visiblePageIndex(scrollPort)
          };
          (_this$props$onMutatio = (_this$props = this.props).onMutation) === null || _this$props$onMutatio === void 0 ? void 0 : _this$props$onMutatio.call(_this$props, mutationEvent);
        }
      }
    };
  }
}), _descriptor6 = _applyDecoratedDescriptor(_class2.prototype, "handleIntervalUpdate", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return () => {
      var _this$props$onInterva, _this$props2;

      const event = {
        isPaused: this.isPaused,
        intervalDuration: this.props.intervalDuration
      };
      (_this$props$onInterva = (_this$props2 = this.props).onIntervalUpdate) === null || _this$props$onInterva === void 0 ? void 0 : _this$props$onInterva.call(_this$props2, event);
    };
  }
}), _descriptor7 = _applyDecoratedDescriptor(_class2.prototype, "resumeCarousel", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return () => {
      if (!this.autoPlayInterval && this.allowAutoPlay) {
        this.isPaused = false;
        this.autoPlayInterval = setInterval(this.advanceCarousel, this.props.intervalDuration);
        this.handleIntervalUpdate();
      }
    };
  }
}), _descriptor8 = _applyDecoratedDescriptor(_class2.prototype, "pauseCarousel", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return () => {
      clearInterval(this.autoPlayInterval);
      this.isPaused = true;
      this.autoPlayInterval = 0;
      this.handleIntervalUpdate();
    };
  }
}), _descriptor9 = _applyDecoratedDescriptor(_class2.prototype, "onInteraction", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return () => {
      if (this.allowAutoPlay) {
        this.allowAutoPlay = false;
        this.pauseCarousel();
      }
    };
  }
})), _class2)) || _class;
//# sourceMappingURL=auto-play-carousel.react-component.js.map